// src/services/StorageAdapter.js
// Abstraction layer for data persistence.
// Default: localStorage. Can be swapped for any backend (REST API, IndexedDB, etc.)
//
// Cache layer (hydrate-on-demand):
//   The cache stores raw JSON strings (immutable) for mutation safety.
//   IMPORTANT: regular getJSON calls do NOT auto-populate the cache.
//   Only hydrate() fills the cache. This ensures direct localStorage writes
//   (e.g., in migration code or tests) remain visible to subsequent reads.
//
//   hydrate(keys) — async batch-read; fills cache before first render
//   setJSON       — writes to backend; also updates cache for hydrated keys
//   removeItem    — writes to backend; also invalidates cache entry
//   clearCache()  — empties entire cache (call at logout)
//
// Sync API (getItem/setItem/removeItem/getJSON/setJSON/keys) is preserved for
// backward compatibility. Consumers are converted to async in INFRA-053+.

class LocalStorageBackend {
  // ── Sync interface (backward compat) ──────────────────────────────────────
  getItem(key) {
    return localStorage.getItem(key);
  }

  setItem(key, value) {
    localStorage.setItem(key, value);
  }

  removeItem(key) {
    localStorage.removeItem(key);
  }

  keys() {
    const result = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null) result.push(key);
    }
    return result;
  }

  // ── Async interface (used by hydrate, ready for SupabaseBackend) ───────────
  async getItemAsync(key) {
    return Promise.resolve(localStorage.getItem(key));
  }

  async setItemAsync(key, value) {
    return Promise.resolve(localStorage.setItem(key, value));
  }

  async removeItemAsync(key) {
    return Promise.resolve(localStorage.removeItem(key));
  }

  async keysAsync() {
    return Promise.resolve(this.keys());
  }
}

class StorageAdapter {
  constructor() {
    this.backend = new LocalStorageBackend();
    this._cache = new Map();
  }

  // Swap backend (e.g., for real DB, REST API, or testing).
  // Clears the cache so stale entries from the old backend are not returned.
  setBackend(backend) {
    this.backend = backend;
    this._cache.clear();
  }

  getBackend() {
    return this.backend;
  }

  // ── Raw string operations ──────────────────────────────────────────────────

  getItem(key) {
    return this.backend.getItem(key);
  }

  // setItem bypasses JSON cache — invalidate any cached entry for this key
  // so a subsequent getJSON call does not return stale data.
  setItem(key, value) {
    this._cache.delete(key);
    this.backend.setItem(key, value);
  }

  removeItem(key) {
    this._cache.delete(key);
    this.backend.removeItem(key);
  }

  // Returns all keys known to the backend
  keys() {
    if (typeof this.backend.keys === 'function') {
      return this.backend.keys();
    }
    return [];
  }

  // ── JSON convenience methods ───────────────────────────────────────────────

  // Returns cached value (if the key was previously hydrated) or reads directly
  // from the backend. getJSON does NOT auto-populate the cache — only hydrate()
  // does. This ensures that direct backend writes (migrations, tests) remain
  // visible without staling the cache.
  // Each call parses a fresh object from the cached string so callers cannot
  // corrupt the cache via mutation.
  getJSON(key, defaultValue = null) {
    if (this._cache.has(key)) {
      try {
        return JSON.parse(this._cache.get(key)); // fresh parse — mutation-safe
      } catch (_e) {
        return defaultValue;
      }
    }
    // Cache miss (or key not hydrated) → read from backend directly
    try {
      const data = this.backend.getItem(key);
      if (data === null || data === undefined) {
        return defaultValue;
      }
      return JSON.parse(data);
    } catch (error) {
      console.error(`[StorageAdapter] Error parsing JSON for key "${key}":`, error);
      return defaultValue;
    }
  }

  // Serialises value and writes to backend. Always populates the cache so
  // subsequent sync reads return the just-written value without a backend
  // round-trip. This is essential for backends whose sync getItem() is a
  // no-op (e.g. SupabaseBackend) — without caching on write, a service that
  // does setJSON → getJSON round-trips in the same tick would read stale
  // (null) data and think the write was lost.
  setJSON(key, value) {
    const json = JSON.stringify(value);
    this._cache.set(key, json);
    this.backend.setItem(key, json);
  }

  // ── Cache management ───────────────────────────────────────────────────────

  // Empties the entire cache. Call this at logout so a subsequent login
  // reads fresh data from the backend.
  clearCache() {
    this._cache.clear();
  }

  // Queries the backend for ALL keys and hydrates every one of them.
  // Essential for SupabaseBackend where sync getItem() returns null — without
  // hydration, entity-specific data (data sources, field mappings, model config,
  // etc.) would appear empty after login.
  // No-op if the backend doesn't support keysAsync().
  async hydrateAll() {
    if (typeof this.backend.keysAsync !== 'function') return;
    try {
      const allKeys = await this.backend.keysAsync();
      if (allKeys.length > 0) {
        await this.hydrate(allKeys);
      }
    } catch (e) {
      console.error('[StorageAdapter] hydrateAll failed:', e);
    }
  }

  // Batch-reads an array of keys from the backend and populates the cache.
  // Skips keys that are already cached. Missing keys (null backend response)
  // are not cached so that defaultValue still works correctly in getJSON.
  // Returns a Promise so callers can await completion before the first render.
  async hydrate(keys) {
    const getAsync = typeof this.backend.getItemAsync === 'function'
      ? (k) => this.backend.getItemAsync(k)
      : (k) => Promise.resolve(this.backend.getItem(k));

    await Promise.all(
      keys.map(async (key) => {
        if (this._cache.has(key)) return; // already cached — skip
        try {
          const data = await getAsync(key);
          if (data !== null && data !== undefined) {
            JSON.parse(data); // validate JSON before caching; throws on bad data
            this._cache.set(key, data); // cache raw JSON string (immutable)
          }
          // null/undefined → key absent in backend → do not cache
        } catch (_e) {
          // JSON parse error or backend error — skip this key silently
        }
      })
    );
  }
}

// Singleton
const storage = new StorageAdapter();

export { LocalStorageBackend };
export default storage;
